import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next"
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import { useHistory, useParams } from 'react-router-dom';

import Toast from 'components/dumb/Toast';
import GlobalHeader from 'components/dumb/GlobalHeader';
import WebcamCapture from 'components/dumb/WebCam/WebCam';
import UnberryModal from 'components/dumb/UnberryModal';
import FullscreenWarning from './FullscreenWarning';
import PermissionsModal from 'components/dumb/PermissionsModal';
import { storage } from 'services/config/storage';
import GameplayApi from '../../services/apis/gameplay';
import {
  capitalizeFirstLetter,
  checkImgProctoringRecency,
  getListOfGames,
  setDocumentDetails,
} from 'utils/helper';
import { updateChatbotData, resolveChatbotType } from 'services/chatbot';
import ProctoringApi from 'services/apis/proctoring';
import candidateFormApi from 'services/apis/form';
import { useGameplayConfig, useScreenRecordingContext } from 'store';
import {
  useFullScreen,
  useGamesLifecycle,
  useIsMobileDevice,
  usePauseResumeTimer,
  useTabSwitch,
  useVideoRecorder,
  useAudioProctoring,
} from 'hooks';
import { GC } from 'utils/postMessage';
import { chatbotTypes, DEFAULT_COMPANY_NAME, PERMISSION_DEVICE_TYPES, PERMISSION_ERRORS } from 'constants/globalConstants';
import { GAME_COMPLETION_STATUS } from 'utils/consts';
import { HIDE_GAME_IFRAME_EVENT, PAUSE_TIMER_EVENT, SHOW_GAME_IFRAME_EVENT, SUPPORT_CHAT_READY_EVENT } from 'constants/helperModalConstants';
import './style.scss';
import { updateFCData } from 'services/freshchat';
import analytics from 'services/config/analytics';

/**
 * DEV ONLY: point the game iframe at a locally-running game build for testing.
 * Set REACT_APP_LOCAL_GAME_URL (e.g. http://localhost:8111) in .env.local to
 * load your local quiz-game instead of the backend-provided deployed URL.
 * Never set this in prod/uat env files — when unset, behaviour is unchanged.
 */
const LOCAL_GAME_URL = process.env.REACT_APP_LOCAL_GAME_URL;

const applyLocalGameOverride = (games) => {
  if (!LOCAL_GAME_URL || !Array.isArray(games)) return games;
  // eslint-disable-next-line no-console
  console.warn(`[dev] Overriding game URLs -> ${LOCAL_GAME_URL}`);
  return games.map((game) => ({ ...game, url: LOCAL_GAME_URL }));
};

const GameLoader = (props) => {
  /**
   * ===================
   * useStates
   * ===================
   */
  const [currentGame, setCurrentGame] = useState({});
  const [availableGames, setAvailableGames] = useState([]);
  const [playedGamesIds, setPlayedGamesIds] = useState(
    storage.get.listOfPlayedGamesIds()
  );
  const [allowedOrigins, setAllowedOrigins] = useState([]);
  const [imgSrc, setImgSrc] = useState('');
  const [isFullScreenEnabled, setIsFullScreenEnabled] = useState(true)
  const [fullScreenWarningVisible, setFullScreenWarningVisible] = useState(false)
  const [userDisqualificationReason, setUserDisqualificationReason] = useState("")
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [gameplayPreRequisitesLoading, setGameplayPreRequisitesLoading] = useState(false)
  const [hideGameIframe, setHideGameIframe] = useState(false);
  const [showSupportTooltip, setShowSupportTooltip] = useState(false);

  /**
   * ===================
   * useRefs
   * ===================
   */
  const frameRef = useRef(null);
  const loaderRef = useRef(null);
  const pauseGameTimerOnMount = useRef(false)
  const webCamRef = useRef(null)

  /**
   * ===================
   * Custom hooks
   * ===================
   */
  const { t } = useTranslation()
  const history = useHistory()
  const { id: linkId } = useParams()
  const isMobile = useIsMobileDevice()
  const {
    isProctored,
    isVideoProctored,
    isFullScreen,
    tabSwitchLimit,
    snapshotInterval,
    fetchConfig,
    configLanguageSettings,
    isScreenProctored,
  } = useGameplayConfig()
  const { isTabWarningFinal } = useTabSwitch({
    tabSwitchLimit,
    setUserDisqualificationReason,
    setShowTabWarning,
    linkId,
  })
  const { disqualifyFullScreen } = useFullScreen({
    setIsFullScreenEnabled,
    isFullScreenEnabled,
    isFullScreen,
    setFullScreenWarningVisible,
    setUserDisqualificationReason,
  })
  const { isPermissionModalVisible: isVideoPermissionModalVisible } = useVideoRecorder()
  const { isPermissionModalVisible: isScreenPermissionModalVisible, initialiseAndStartRecording } = useScreenRecordingContext()
  usePauseResumeTimer(currentGame)
  useAudioProctoring() // [AUDIO PROCTORING v2] runs only when isAudioProctored; streams audio + VAD events to unstream

  /**
   * ===================
   * constant variables
   * ===================
   */
  const { configLanguage } = configLanguageSettings

  /**
 * ===================
 * functions
 * ===================
 */

  const redirectToExternalUrl = (url) => {
    window.location.replace(url);
  };

  const markGameplayAsEnd = async (disqualifyData = {}) => {
    if (document.title.toLowerCase().includes(DEFAULT_COMPANY_NAME)) {
      document.title = `Unberry`;
    } else {
      const [companyName] = setDocumentDetails();
      document.title = `${capitalizeFirstLetter(companyName)}`;
    }
    const callbackUrl = storage.get.url();
    storage.destroy.url();
    try {
      const sendEmail = !callbackUrl;
      const response = await GameplayApi.quitUnberryGamePlay(linkId, sendEmail, disqualifyData);
      if (response?.skippedGames?.length > 0) {
        initSkippedGames(response.skippedGames)
        return
      }

      // sending postmessage event for iframe communication
      // Need to send this for SDK purpose
      if (window.location !== window.parent.location) {
        GC.sendGameplayEndMessage()
      }

      if (callbackUrl) {
        redirectToExternalUrl(callbackUrl);
      } else {
        const { id: userId } = storage.get.fcData()
        history.push({
          pathname: `/end/${userId}/${linkId}`, state: {
            disqualifyReason: disqualifyData?.disqualifyReason ?? ""
          }
        });
      }
    } catch (error) {
      Toast('error', error.message);
      if (callbackUrl) {
        redirectToExternalUrl(callbackUrl);
      } else {
        history.push(`/home/${linkId}`);
      }
      console.log('error', error);
    }
  };

  const initSkippedGames = (skippedGames = []) => {
    if (skippedGames.length > 0) {
      skippedGames = skippedGames
        .map(skippedGame => {
          skippedGame = {
            ...skippedGame,
            url: skippedGame?.deeplink,
            name: skippedGame?.name ?? skippedGame?.title,
            uniqueId: `${new Date().getTime() + Math.random()}`,
          }
          delete skippedGame?.deeplink
          delete skippedGame?.title
          return skippedGame
        })

      skippedGames = applyLocalGameOverride(skippedGames)
      const unskippedAvailableGames = availableGames
        .filter(availableGame => !skippedGames?.find(skippedGame => availableGame.id === skippedGame.id))
      const updatedAvailableGames = [...unskippedAvailableGames, ...skippedGames]
      setAvailableGames(updatedAvailableGames)
      setAllowedOrigins(updatedAvailableGames.map(({ url }) => url))

      const latestPlayedGameIds = storage.get.listOfPlayedGamesIds()
      const updatedPlayedGameIds = latestPlayedGameIds
        .filter(playedGameId => !skippedGames?.find(skippedGame => skippedGame.id === playedGameId))
      storage.set.listOfPlayedGamesIds(updatedPlayedGameIds)
      setPlayedGamesIds(updatedPlayedGameIds)

      if (loaderRef.current) loaderRef.current.style.display = 'flex'
      const [nextGame] = skippedGames
      setCurrentGame(nextGame)
    } else {
      markGameplayAsEnd()
    }
  }

  const initGame = useCallback(
    (updatedPlayedGamesIds = []) => {
      // saving update ids
      storage.set.listOfPlayedGamesIds(updatedPlayedGamesIds);
      setPlayedGamesIds(updatedPlayedGamesIds);

      // filtering unplayed games based on gameIds
      // Todo: unplayedGames should not have duplicates entries
      // Todo: pass gameComplete flag at the end of each game api call
      const unplayedGames = availableGames.filter(
        (game) => !updatedPlayedGamesIds.includes(game.id)
      );

      if (unplayedGames.length > 0) {
        // load first game from the unplayedGames
        if (loaderRef.current) loaderRef.current.style.display = 'flex';
        const [nextGame] = unplayedGames;

        // No need to use this now because of key on iframe
        // if (
        //   nextGame?.url === currentGame?.url &&
        //   nextGame.id !== currentGame?.id
        // ) {
        //   // to re-render the frame to mount the game again
        //   setCurrentGame({});
        // }

        setCurrentGame(nextGame);

      } else if (
        unplayedGames.length === 0 &&
        availableGames.length !== 0 &&
        updatedPlayedGamesIds.length >= availableGames.length
      ) {
        analytics.track('all_games_completed', { game_count: availableGames.length });
        markGameplayAsEnd();
      }
    },
    [availableGames, history, linkId, currentGame]
  );

  const handleHideGameEvent = () => setHideGameIframe(true)
  const handleShowGameEvent = () => setHideGameIframe(false)

  // Track when the played-games count grows -> a game was completed.
  // playedGamesIds can be null for a fresh candidate (nothing in storage yet).
  const prevPlayedCountRef = useRef(playedGamesIds?.length ?? 0)
  useEffect(() => {
    const playedCount = playedGamesIds?.length ?? 0
    if (playedCount > prevPlayedCountRef.current) {
      analytics.track('game_completed', { completed_count: playedCount })
    }
    prevPlayedCountRef.current = playedCount
  }, [playedGamesIds])

  /**
   * ===================
   * useEffects
   * ===================
   */
  useEffect(() => {
    if (!isMobile) return
    const showGameplayOnly = storage.get.showGameplayOnly()
    if (showGameplayOnly || storage.get.supportTooltipDismissed()) return

    // If chat widget is already initialized (navigated from rules), show tooltip after delay
    if (window.fcWidget?.isInitialized?.() || window.chatWidget?.isInitialized?.()) {
      const timer = setTimeout(() => setShowSupportTooltip(true), 1200)
      return () => clearTimeout(timer)
    }

    const handleChatReady = () => {
      setTimeout(() => setShowSupportTooltip(true), 1200)
    }
    window.addEventListener(SUPPORT_CHAT_READY_EVENT, handleChatReady)
    return () => window.removeEventListener(SUPPORT_CHAT_READY_EVENT, handleChatReady)
  }, [])

  useEffect(() => {
    if (!showSupportTooltip) return
    const timer = setTimeout(() => {
      storage.set.supportTooltipDismissed()
      setShowSupportTooltip(false)
    }, 8000)
    return () => clearTimeout(timer)
  }, [showSupportTooltip])

  const dismissSupportTooltip = () => {
    storage.set.supportTooltipDismissed()
    setShowSupportTooltip(false)
  }

  useEffect(() => {
    window.addEventListener(HIDE_GAME_IFRAME_EVENT, handleHideGameEvent)
    window.addEventListener(SHOW_GAME_IFRAME_EVENT, handleShowGameEvent)
    return () => {
      window.removeEventListener(HIDE_GAME_IFRAME_EVENT, handleHideGameEvent)
      window.removeEventListener(SHOW_GAME_IFRAME_EVENT, handleShowGameEvent)
    }
  }, [currentGame])

  useEffect(() => {
    (async () => {
      if (userDisqualificationReason?.length > 0) {
        markGameplayAsEnd({
          disqualifyReason: userDisqualificationReason
        })
      }
    })()
  }, [userDisqualificationReason])

  useEffect(() => {
    if (hideGameIframe) {
      // Setting a timeout for GC to load iframe and dispatch pause timer event
      setTimeout(() => {
        window.dispatchEvent(new Event(PAUSE_TIMER_EVENT))
      }, 1000)
    }
  }, [hideGameIframe])

  useEffect(() => {
    let didCancel = false; // to avoid state update after component unmount
    const getGames = async () => {
      console.log(`[Gettig list of games] for link: `, linkId);
      const gamesFromApi = await getListOfGames(linkId); // get list of games for a linkId
      const games = applyLocalGameOverride(gamesFromApi);
      console.log(`[Games recieved] success: `, JSON.stringify(games));
      if (games) {
        analytics.track('games_list_loaded', { game_count: games.length });
        const completedGameIds = games.filter(game => game.status === GAME_COMPLETION_STATUS.COMPLETED)?.map(game => game.id)
        storage.set.listOfPlayedGamesIds(completedGameIds)
        setPlayedGamesIds(completedGameIds)
        if (!didCancel) {
          setAvailableGames(games);
          setAllowedOrigins(games.map(({ url }) => url));
        }
      } else {
        analytics.track('games_list_failed');
        // if games data is not present logout the user
        history.push(`/home/${linkId}`);
        storage.destroy.all();
      }
    };

    const lsLinkId = storage.get.linkId()
    if (lsLinkId != linkId) {
      history.push(`/home/${linkId}`)
      storage.destroy.all()
    } else {
      getGames();
    }

    return () => {
      didCancel = true;
    };
  }, []);

  useEffect(() => {
    initGame(playedGamesIds);
  }, [availableGames]);

  useEffect(() => {
    if (currentGame.name) {
      const { name: gameName } = currentGame
      analytics.track('game_started', { game_id: currentGame.id, game_name: gameName })
      if (document.title.toLowerCase().includes(DEFAULT_COMPANY_NAME)) {
        document.title = `Unberry • ${gameName}`
      } else {
        const [companyName] = setDocumentDetails()
        document.title = `${capitalizeFirstLetter(companyName)} • ${gameName}`
      }
      const showGameplayOnly = storage.get.showGameplayOnly()
      if (!showGameplayOnly) {
        const orgInfo = storage.get.orgInfo()
        const chatbot = resolveChatbotType(orgInfo?.organizationConfig?.supportChatbotType)
        if (chatbot === chatbotTypes.FRESHCHAT) {
          updateFCData({ meta: { gameName } }, isMobile)
        } else if (chatbot === chatbotTypes.UNBERRY) {
          updateChatbotData({ metadata: { gameName } })
        }
      }
      checkGameplayPreRequisites()
    }
  }, [currentGame])

  /**
   * For cases where, GC triggers the permission before the game has mounted.
   * Send this value in GAME_MOUNT event in useGamesLifecycle
   */
  useEffect(() => {
    if (isScreenPermissionModalVisible || isVideoPermissionModalVisible) {
      pauseGameTimerOnMount.current = true
    }
  }, [isScreenPermissionModalVisible, isVideoPermissionModalVisible])

  useGamesLifecycle({
    allowedOrigins,
    currentGame,
    linkId,
    loaderRef,
    playedGamesIds,
    initGame,
    pauseGameTimerOnMount,
    isFullScreen,
    isMobile,
    disqualifyFullScreen,
    configLanguage,
    hideGameIframe
  })

  const checkGameplayPreRequisites = async () => {
    try {
      setGameplayPreRequisitesLoading(true)
      const { positionConfigRes, linkConfigRes } = await fetchConfig(false)
      if (positionConfigRes?.success) {
        if (!!positionConfigRes?.positionConfig?.customizableForm) {
          const candidateFormData = await candidateFormApi.getCandidateFormData(linkId)
          if (candidateFormData?.success) {
            if (candidateFormData?.userData === null) {
              history.replace(`/form/${linkId}`)
              return
            }
          }
        }
        if (linkConfigRes?.success) {
          if (!!linkConfigRes?.linkPositionConfig ? !!linkConfigRes?.linkPositionConfig?.proctoring
            : !!positionConfigRes?.positionConfig?.proctoring
          ) {
            const response = await ProctoringApi.getUserReferenceImg(linkId)
            if (response.status === 404) {
              history.replace(`/rules/${linkId}`)
            }
            const isStale = checkImgProctoringRecency(linkId)
            if (isStale) webCamRef.current.showErrorModal(PERMISSION_ERRORS.API_FAILURE)
          }
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      setGameplayPreRequisitesLoading(false)
    }
  }

  return (
    <section className="game-container bg-color-primary flex flex-col">
      {isProctored && (
        <WebcamCapture
          ref={webCamRef}
          imgSrc={imgSrc}
          setImgSrc={setImgSrc}
          linkId={linkId}
          snapshotInterval={snapshotInterval}
          pauseGameTimer={() => {
            pauseGameTimerOnMount.current = true
          }}
        />
      )}
      {availableGames.length > 0 &&
        playedGamesIds.length < availableGames.length ? (
        <GlobalHeader
          currentLoadedGame={currentGame.name}
          currentGameIndex={availableGames.findIndex(
            (game) => game.id === currentGame.id
          )}
          totalAvailableGames={availableGames}
          isFullScreen={isFullScreen}
          isFullScreenEnabled={isFullScreenEnabled}
          isProctored={isVideoProctored}
        />
      ) : null}
      <div id="game-window">
        <div className="game-wrapper">
          <div ref={loaderRef} className="game-loader flex items-center justify-center absolute top-0 left-0">
            <Spin
              indicator={<LoadingOutlined spin style={{ color: "#fff", fontSize: 28 }} />}
            />
          </div>
          <iframe
            key={currentGame?.uniqueId ?? currentGame?.id}
            ref={frameRef}
            id="game-frame"
            src={hideGameIframe ? null : currentGame?.url}
            title="gameFrame"
            allow="microphone; camera; autoplay"
          />
          <UnberryModal
            visible={showTabWarning}
            onCancel={() => setShowTabWarning(false)}
            footer={false}
            className={'warning-modal'}
          >
            <div className='flex flex-col items-center align-center'>
              {
                isTabWarningFinal ? <p className='size-32'>⚠️</p> : <></>
              }
              <p className="color-primary font-bold size-32 mb-20">
                {t("tab_switch.heading")}
              </p>
              {
                isTabWarningFinal
                  ? <>
                    <p className="color-primary font-bold size-18 verify" style={{ lineHeight: '26.5px' }}>
                      {t("tab_switch.final_subtext")}
                    </p>
                  </>
                  : <>
                    <p className="color-primary font-bold size-18 verify" style={{ lineHeight: '26.5px', whiteSpace: 'pre-line' }}>
                      {t("tab_switch.subtext")}
                    </p>
                  </>
              }
              <Button
                className='mt-10'
                onClick={() => setShowTabWarning(false)}
              >
                {t("tab_switch.understood")}
              </Button>
            </div>
          </UnberryModal>
          <FullscreenWarning
            fullScreenWarningVisible={fullScreenWarningVisible}
            setFullScreenWarningVisible={setFullScreenWarningVisible}
            disqualifyFullScreen={disqualifyFullScreen}
          />
          <PermissionsModal
            visible={!!(isVideoPermissionModalVisible)}
            type={isVideoPermissionModalVisible}
            device={PERMISSION_DEVICE_TYPES.CAMERA_MIC}
            handleOnConfirm={null}
          />
          <PermissionsModal
            visible={!!(isScreenPermissionModalVisible)}
            type={isScreenPermissionModalVisible}
            device={PERMISSION_DEVICE_TYPES.SCREEN}
            handleOnConfirm={initialiseAndStartRecording}
          />
          {
            gameplayPreRequisitesLoading ? <>
              <div className="game-loader flex items-center justify-center absolute top-0 left-0">
                <Spin
                  indicator={<LoadingOutlined spin style={{ color: "#fff", fontSize: 28 }} />}
                />
              </div>
            </> : <></>
          }
        </div>
      </div>
      {/* {showSupportTooltip && (
        <div className="support-tooltip" onClick={dismissSupportTooltip}>
          <p className="support-tooltip__text">
            {t("support_tooltip.message")}
          </p>
        </div>
      )} */}
    </section>
  );
};

export default GameLoader;
